import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  AppDeleteModal,
  AppFileUploader,
  AppInput,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import Translator from '../../../../../components/helpers/Translator';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import colors from '../../../../../../constants/colors';
import minions from '../../../../../../minions/minions';
import _ from 'lodash';

export default function PlantObligationClientElementsTab(props) {
  const { plantObligationID } = props;
  const { moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const clientPlantObligationClientElements = useSelector(
    (s) => s.currentPage?.clientPlantObligationClientElements
  );
  const plantObligationClientElementsTabs = useSelector(
    (s) => s.currentPage?.plantObligationClientElementsTabs
  );
  const plantObligationClientElements = useSelector(
    (s) => s.currentPage?.plantObligationClientElementsTabs?.[plantObligationID]
  );
  const [selectorElements, setSelectorElements] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [editElement, setEditElement] = useState();
  const [isEditMode, setIsEditMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [assignValidationSchema] = useState(
    Yup.object().shape({
      plantObligationClientElements: Yup.array()
        .of(
          Yup.number()
            .nullable()
            .required('Requerido')
        )
        .min(1, 'Selecciona al menos un elemento'),
    })
  );
  const [editValidationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required('Requerido'),
    })
  );

  // > LOAD INFORMATION TAB
  const loadPlantObligationClientElementsTab = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantObligationClientElement_tab"
          ),
          { moduleBridgeHex, plantObligationID }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                "clientPlantObligationClientElements",
                data?.clientPlantObligationClientElements,
                true
              )
            );
            dispatch(
              currentPageAction.change("plantObligationClientElementsTabs", {
                [plantObligationID]: data?.plantObligationClientElementsTabs,
              })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantObligationID]
  );

  useEffect(() => {
    loadPlantObligationClientElementsTab();
  }, []);

  useEffect(() => {
    if (clientPlantObligationClientElements !== undefined)
      setSelectorElements(
        clientPlantObligationClientElements?.map((d) => d?.selector)
      );
  }, [clientPlantObligationClientElements]);
  // < LOAD INFORMATION TAB

  // > ASSIGN
  const handleAssign = useCallback(
    async (values, { resetForm }) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantObligationClientElement_assign"
          ),
          { moduleBridgeHex, plantObligationID, ...values }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            resetForm();
            dispatch(
              currentPageAction.change("plantObligationClientElementsTabs", {
                [plantObligationID]: data,
              })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    []
  );
  // < ASSIGN

  // > UNASSIGN
  const handleUnassign = useCallback(
    async (id) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantObligationClientElement_unassign"
          ),
          { moduleBridgeHex, plantObligationID, id }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("plantObligationClientElementsTabs", {
                [plantObligationID]: data,
              })
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantObligationID]
  );

  useEffect(() => {
    loadPlantObligationClientElementsTab();
  }, []);
  // < UNASSIGN

  // > EDITION
  const handleEdit = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantObligationClientElement_set"
          ),
          minions.forms.toFormData({
            moduleBridgeHex,
            plantObligationID,
            plantObligationIDs: Object.keys(plantObligationClientElementsTabs),
            ...values,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                "clientPlantObligationClientElements",
                data?.clientPlantObligationClientElements,
                true
              )
            );
            dispatch(
              currentPageAction.change(
                "plantObligationClientElementsTabs",
                data?.plantObligationClientElementsTabs,
                true
              )
            );
            setEditElement(undefined);
            setIsEditMode(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, plantObligationID, plantObligationClientElementsTabs]
  );
  // < EDITION

  // > DELETE
  const handleDelete = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_plantObligationClientElement_delete"
          ),
          {
            moduleBridgeHex,
            plantObligationIDs: Object.keys(plantObligationClientElementsTabs),
            id: editElement?.id,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                "clientPlantObligationClientElements",
                data?.clientPlantObligationClientElements,
                true
              )
            );
            dispatch(
              currentPageAction.change(
                "plantObligationClientElementsTabs",
                data?.plantObligationClientElementsTabs,
                true
              )
            );
            setEditElement(undefined);
            setIsEditMode(false);
            setConfirmDelete(undefined);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, editElement, plantObligationClientElementsTabs]
  );
  // < DELETE

  const handleDownloadFile = useCallback(async (id, salt) => {
    axios
      .post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantObligationClientElement_file_download"
        ),
        {
          id,
        }
      )
      .then(({ data }) => {
        if (isMounted.current) {
          window.open(data, "_blank");
        }
      })
      .catch((e) => {
        if (e.response.status === 404) {
          AppNotificationAlert({
            title: "Error",
            message: e.response.data,
            type: "danger",
            duration: 2000,
          });
        } else {
          dispatch(errorHandler(e));
        }
      });
  }, []);

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <Container>
      {isEditMode ? (
        <>
          <Formik
            initialValues={{
              id: editElement?.id || null,
              name: editElement?.name || '',
              description: editElement?.description || '',
              files: [],
              isDeletedFile: false,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={editValidationSchema}
            onSubmit={handleEdit}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <form onSubmit={handleSubmit}>
                <AppInput
                  title="Nombre"
                  icon={<AppRemixIcon icon="font-color" noVariants />}
                  onChange={(v) => setFieldValue('name', v)}
                  value={values.name}
                  error={errors.name}
                  required
                />
                <AppTextArea
                  title="Descripción"
                  icon={<AppRemixIcon icon="list-check" noVariants />}
                  onChange={(v) => setFieldValue('description', v)}
                  value={values.description}
                  error={errors.description}
                />
                <AppFileUploader
                  placeholder={
                    values.files
                      ? values?.files[0]?.name
                      : ReactDOMServer.renderToString(
                          <Translator file="general" name="select.a.file" />
                        )
                  }
                  onChange={(opt) => setFieldValue('files', opt)}
                  error={errors?.files}
                />
                {editElement && editElement?.files && (
                  <FileList>
                    {_.map(editElement?.files, (f, salt) => (
                      <li
                        key={salt}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="admin"
                            name="general.click-delete"
                          />
                        )}
                        onClick={() =>
                          setFieldValue('isDeletedFile', !values.isDeletedFile)
                        }
                      >
                        <AppRemixIcon
                          className="pointer mr-3"
                          icon="delete-bin"
                        />
                        {minions.files.fileIcon(f.name, true, null, 'mr-2')}
                        <FileName isDeleted={values.isDeletedFile}>
                          {f.name}
                        </FileName>
                      </li>
                    ))}
                  </FileList>
                )}
                <div className="d-flex justify-content-end mb-2">
                  {editElement && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-xs mr-1"
                      title={ReactDOMServer.renderToString(
                        <Translator file="user" name="user.delete" />
                      )}
                      onClick={() => {
                        setConfirmDelete(true);
                      }}
                    >
                      <AppRemixIcon icon="delete-bin" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-xs mr-1"
                    onClick={() => {
                      setEditElement(undefined);
                      setIsEditMode(false);
                    }}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary btn-xs">
                    {editElement ? 'Editar' : 'Crear'}
                  </button>
                </div>
              </form>
            )}
          </Formik>
          <AppDeleteModal
            show={confirmDelete}
            title={ReactDOMServer.renderToString(
              <Translator file="general" name="general.delete" />
            )}
            onClose={() => setConfirmDelete(false)}
            onConfirm={handleDelete}
          />
        </>
      ) : (
        <div>
          <Formik
            initialValues={{
              plantObligationClientElement: undefined,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={assignValidationSchema}
            onSubmit={handleAssign}
          >
            {({ setFieldValue, handleSubmit, values, resetForm }) => (
              <form onSubmit={handleSubmit}>
                <AppSingleSelect
                  icon={<AppRemixIcon icon="apps-2" />}
                  options={selectorElements || []}
                  value={values?.plantObligationClientElement}
                  onChange={(opt) =>
                    setFieldValue('plantObligationClientElement', opt)
                  }
                  isLoading={selectorElements === undefined}
                  hasSearchBox
                  hasSelectOptions
                  isClearable
                />
                <div className="d-flex justify-content-end mb-2">
                  {values?.plantObligationClientElement ? (
                    <button
                      type="button"
                      className="btn btn-outline-success btn-xs mr-1"
                      onClick={() => {
                        setEditElement(
                          clientPlantObligationClientElements.find(
                            (e) => e.id === values?.plantObligationClientElement
                          )
                        );
                        setIsEditMode(true);
                      }}
                    >
                      Editar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-success btn-xs mr-1"
                      onClick={() => setIsEditMode(true)}
                    >
                      Crear
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary btn-xs"
                    disabled={plantObligationClientElements?.length > 0 || !values?.plantObligationClientElement}
                  >
                    <Translator
                      file="module-legalCompliance"
                      name="general.assign"
                    />
                  </button>
                </div>
              </form>
            )}
          </Formik>
          <div className="clearfix" />
          {plantObligationClientElements ? (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th className="col-lg-4">Nombre</th>
                  <th className="col-lg-4">Descripción</th>
                  <th className="col-lg-2 text-center">Archivo</th>
                  <th className="col-lg-2"></th>
                </tr>
              </thead>
              <tbody>
                {plantObligationClientElements.map((poceID) => {
                  const poce = clientPlantObligationClientElements.find(
                    (e) => e.id === poceID
                  );

                  return (
                    <tr key={poceID}>
                      <td>{poce?.name}</td>
                      <td>{poce?.description}</td>
                      <td className="text-center">
                        {!_.isEmpty(poce?.files) ? (
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleDownloadFile(poceID)}
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="download"
                              />
                            )}
                          >
                            <AppRemixIcon icon="download-2" filled />
                          </button>
                        ) : (
                          '--'
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleUnassign(poceID)}
                        >
                          <AppRemixIcon icon="delete-bin" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="alert alert-light">
              <Translator
                file="module-legalCompliance"
                name="no.elements.assigned"
              />
            </div>
          )}
        </div>
      )}
    </Container>
  );
}

const Container = styled.div`
  height: 400px;
  tr > td {
    padding: 4px;
    font-size: 12px;
    & > div {
      height: 20px;
      overflow-y: hidden;
      text-overflow: ellipsis;
      cursor: pointer;
    }
  }
`;

const FileList = styled.ul`
  list-style: none;
  margin: 0;
  padding-left: 6px;
  overflow-y: auto;
  max-height: 113px;
  li:hover {
    background-color: rgba(232, 232, 232, 0.8);
  }
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;

const FileName = styled.span`
  text-decoration: ${(props) => (props.isDeleted ? 'line-through' : 'unset')};
`;
